import type { AlertMessage } from '../components/alert-display/alert-message';

/**
 * Triggers a browser download for a Blob with the given filename.
 */
export const downloadBlobAsFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
};

/**
 * Parses a Blob error response into AlertMessage array.
 * Handles RFC 7807 ProblemDetails (e.g. 408 timeout), plain string arrays, and raw text.
 */
export const parseBlobDownloadError = async(blob: Blob): Promise<AlertMessage[]> => {
  const text: string = await blob.text();
  try {
    const parsed: unknown = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return (parsed as string[]).map(msg => ({ type: 'danger' as const, msg }));
    }
    // Plain JSON string (e.g. WriteAsJsonAsync with a string value)
    if (typeof parsed === 'string') {
      return [{ type: 'danger', msg: parsed }];
    }
    // RFC 7807 ProblemDetails — use 'detail' if present, fall back to 'title'
    if (parsed !== null && typeof parsed === 'object') {
      const problem = parsed as Record<string, unknown>;
      const msg = (problem.detail as string | undefined) ?? (problem.title as string | undefined);
      if (msg) {
        return [{ type: 'danger', msg }];
      }
    }
  } catch {
    // Not JSON
  }
  return [{ type: 'danger', msg: text || 'Failed to download file.' }];
};
